// Shared toast-string formatter for recipe creation paths. Both the
// bulk-import modal and the verbal-authoring dictate modal land on the
// same `/api/mfg/recipes/bulk-create` endpoint and produce the same
// summary shape; this helper keeps the success-toast phrasing aligned
// across both surfaces so the panel renders identical UX.
//
// Plan reference: `docs/planning/mfg_recipe_verbal_authoring.md` §3.5.

import type { MfgRecipeBulkCreateResponse } from "@/lib/manufacturing/types/recipeImport";

export type RecipeCreationToastVariant = "imported" | "created";

/**
 * Build a · -separated toast string summarizing a bulk-create response.
 *
 * @param summary - the `summary` field from a successful bulk-create response
 * @param variant - "imported" for the bulk-import modal (N recipes at once),
 *                  "created" for the dictate modal (always 1 recipe).
 *                  Changes only the leading verb; pending / skipped phrasing
 *                  stays the same.
 */
export function formatRecipeCreationToast(
  summary: MfgRecipeBulkCreateResponse["summary"],
  variant: RecipeCreationToastVariant,
): string {
  const created = summary.created.length;
  const updated = summary.updated?.length ?? 0;
  const touched = [...summary.created, ...(summary.updated ?? [])];
  const pending = touched.reduce((acc, c) => acc + c.lines_pending, 0);
  const matched = touched.reduce((acc, c) => acc + c.lines_matched, 0);
  const skipped = summary.skipped.length;

  const parts: string[] = [];
  if (variant === "imported") {
    const bits: string[] = [];
    if (created > 0) bits.push(`${created} created`);
    if (updated > 0) bits.push(`${updated} updated`);
    parts.push(
      bits.length > 0
        ? `Imported ${bits.join(", ")}`
        : "Imported 0 recipes",
    );
  } else {
    parts.push(
      created === 1
        ? `Recipe created (${matched} matched line${matched === 1 ? "" : "s"})`
        : `Created ${created} recipes`,
    );
  }
  if (pending > 0) {
    parts.push(`${pending} pending line${pending === 1 ? "" : "s"} need reconcile`);
  }
  if (summary.needs_review && touched.length > 0) {
    parts.push("flagged for review");
  }
  if (skipped > 0) parts.push(`${skipped} skipped`);
  return parts.join(" · ");
}
